import { API_BASE_URL } from "src/config/apiBase";
import React, { useState, useEffect } from 'react';
import { useNavigate } from "react-router-dom";
import '../../CSS/Global.css';
import { Document, Page, Text, View, StyleSheet, Image, Link, pdf } from "@react-pdf/renderer";
import { saveAs } from "file-saver";
import { useParams } from "react-router-dom";
import logoImgText from '../../Img/100BILLION_TECH_name.png';
import logoImg from '../../Img/100BILLION_ICON.png';
import { getDecryptedUserFromCookie } from '../utils/getCookie';
import Swal from 'sweetalert2';
import { parseDocument } from 'htmlparser2';
import { DomUtils } from 'htmlparser2';
import dayjs from "dayjs";
import {
    ChevronLeft,
    ChevronRight,
    ChevronsLeft,
    ChevronsRight,
    Search,
    X
} from "lucide-react";
import { useSearch } from '../../SearchContext';
import { useFilter } from '../../FilterContext';
import FilterDrawer from '../FilterDrawer';
import Loader from '../Loader';
import { motion } from 'framer-motion';
import { decryptToken } from '../utils/getToken';


const Invoice = () => {
    const navigate = useNavigate();
    const [isDropdownOpen, setDropdownOpen] = useState(false);
    const [isImportOpen, setImportOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [loading, setLoading] = useState(false);
    const [selectedFilter, setSelectedFilter] = useState("All invoice");
    const [invoices, setInvoices] = useState([]);
    const [downloadingId, setDownloadingId] = useState(null);
    const [downloadedId, setDownloadedId] = useState(null);
    const [load, setLoad] = useState(false)
    const url = API_BASE_URL
    const isLoggedIn = getDecryptedUserFromCookie("gta_mainstateuser");
    const [currentPage, setCurrentPage] = useState(1);
    const { globalSearch } = useSearch();
    const { entriesPerPage, sortOrder, showDrawer, setShowDrawer, customDate } = useFilter();
    const [rangeType, setRangeType] = useState('0');
    const [customRange, setCustomRange] = useState({ from: '', to: '' });
    const token = decryptToken("accessToken") || ""
    const [showSearchBar, setShowSearchBar] = useState(false);

    const safeDate = (dateStr) => {
        try {
            if (!dateStr || dateStr === "0000-00-00") return "N/A";

            const parsedDate = new Date(dateStr);
            if (isNaN(parsedDate.getTime())) return "N/A";

            return parsedDate.toLocaleDateString("en-IN", {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
            });
        } catch {
            return "N/A";
        }
    };

    const outstandingAmount = (inv) =>
        parseFloat(
            inv.total_pending ?? inv.pending ?? inv.adjusted_pending ?? 0
        ) || 0;

    const grandTotalAmount = (inv) =>
        parseFloat(inv.total_grand_total ?? inv.grand_total ?? 0) || 0;

    const collectedAmount = (inv) => {
        const g = grandTotalAmount(inv);
        const o = outstandingAmount(inv);
        return Math.max(0, g - o);
    };

    const invoiceActivityDate = (inv) => {
        const raw =
            inv.createdAt ||
            inv.invoice_date ||
            inv.created_range?.to ||
            inv.created_range?.from;
        const parsed = raw ? new Date(raw) : null;
        return parsed && !isNaN(parsed.getTime()) ? parsed : null;
    };

    const invoicesForSummary = Array.isArray(invoices)
        ? invoices.filter(inv => inv.dpstatus !== "Draft")
        : [];

    const totalReceivablesPending = invoicesForSummary.reduce(
        (acc, invoice) => acc + outstandingAmount(invoice),
        0
    );

    const today = new Date().toISOString().split("T")[0];

    const dueTodayPendingTotal = invoices.reduce((acc, invoice) => {
        if (!invoice.due_date || invoice.due_date === "0000-00-00") return acc;

        const dueDateObj = new Date(invoice.due_date);
        if (isNaN(dueDateObj.getTime())) return acc;

        const dueDate = dueDateObj.toISOString().split("T")[0];
        return dueDate === today ? acc + outstandingAmount(invoice) : acc;
    }, 0);


    const handleEdit = (invoice) => {
        navigate('/new-invoice', { state: { invoice } });
    };

    const getStatus = (invoice) => {
        const today = new Date().toISOString().split("T")[0];
        const dueDate = invoice.due_date;

        const pending = outstandingAmount(invoice);

        if (pending === 0) return { label: "Paid", color: "green" };

        if (!dueDate || dueDate === "0000-00-00") return { label: "Pending", color: "gray" };

        try {
            if (new Date(dueDate).toISOString().split("T")[0] < today) {
                return { label: "Overdue", color: "red" };
            }
        } catch {
            return { label: "Pending", color: "gray" };
        }

        return { label: "Pending", color: "gray" };
    };

    const overduePendingTotal = invoices.reduce((acc, invoice) => {
        if (invoice.dpstatus === "Draft") return acc;
        const { label } = getStatus(invoice);
        return label === "Overdue" ? acc + outstandingAmount(invoice) : acc;
    }, 0);

    const payableWithin30DaysTotal = invoices.reduce((acc, invoice) => {
        if (invoice.dpstatus === "Draft") return acc;
        const dueDate = new Date(invoice.due_date);
        const today = new Date();
        const diffInDays = (dueDate - today) / (1000 * 60 * 60 * 24);

        if (diffInDays > 0 && diffInDays <= 30) {
            return acc + outstandingAmount(invoice);
        }
        return acc;
    }, 0);

    useEffect(() => {
        const fetchInvoices = async () => {
            setLoad(true); // start loading
            try {
                const res = await fetch(`${url}/newinvoice/merged/${isLoggedIn.main_id}`, {
                    method: "GET",
                    headers: {
                        Authorization: `Bearer ${token}`,
                    }
                });
                const data = await res.json();
                if (Array.isArray(data)) {
                    // const sortedInvoices = data.sort((a, b) => a.order_number - b.order_number).reverse();
                    setInvoices(data);
                } else {
                    console.error("Fetched data is not an array:", data);
                    setInvoices([]);
                }
            } catch (error) {
                console.error("Failed to fetch invoices:", error);
                setInvoices([]);
            } finally {
                setLoad(false); // stop loading no matter success or failure
            }
        };

        fetchInvoices();
    }, []);



    // Toggle the dropdown list
    const toggleDropdown = () => {
        setDropdownOpen(!isDropdownOpen);
    };

    // Toggle the import list
    const toggleImport = () => {
        setImportOpen(!isImportOpen);
    };

    // Handle search input change
    const handleSearch = (event) => {
        setSearchQuery(event.target.value);
    };

    let filteredInvoices = [...invoices];

    // 1. Filter by date
    if (customDate.from || customDate.to) {
        filteredInvoices = filteredInvoices.filter((invoice) => {
            const createdAt = new Date(invoice.createdAt || invoice.invoice_date); // fallback
            const from = customDate.from ? new Date(customDate.from) : null;
            const to = customDate.to ? new Date(customDate.to) : null;

            return (!from || createdAt >= from) && (!to || createdAt <= to);
        });
    }

    // 2. Sort by date
    filteredInvoices.sort((a, b) => {
        const dateA = new Date(a.createdAt || a.invoice_date);
        const dateB = new Date(b.createdAt || b.invoice_date);
        return sortOrder === 'asc' ? dateA - dateB : dateB - dateA;
    });

    const searchText = showSearchBar ? searchQuery : globalSearch;

    // 3. Global Search + Filter
    filteredInvoices = filteredInvoices.filter((invoice) => {
        const search = searchText.toLowerCase();
        const status = getStatus(invoice).label;
        const createdBy = invoice.created_by?.toLowerCase() || "import";

        const matchesSearch = (
            invoice.customer_name?.toLowerCase().includes(search) ||
            invoice.invoice_number?.toLowerCase().includes(search) ||
            invoice.order_number?.toString().includes(search) ||
            invoice.invoice_date?.toLowerCase().includes(search) ||
            invoice.due_date?.toLowerCase().includes(search)
        );

        if (!matchesSearch) return false;

        switch (selectedFilter) {
            case "Active invoice":
                return status === "Pending";
            case "Paid invoice":
                return status === "Paid";
            case "Overdue invoice":
                return status === "Overdue";
            case "My invoice":
                return createdBy === "admin";
            case "My Active invoice":
                return createdBy === "admin" && status === "Pending";
            case "My Paid invoice":
                return createdBy === "admin" && status === "Paid";
            case "My Overdue invoice":
                return createdBy === "admin" && status === "Overdue";
            case "All invoice":
                return true;
            default:
                // treat any unknown filter as "All invoice"
                return true;
        }
    });

    const SkeletonLoader = () => {
        return (
            <div>
                {Array(10)
                    .fill("")
                    .map((_, index) => (
                        <div className="skeleton-row" key={index}>
                            <div className="skeleton"></div>
                            <div className="skeleton"></div>
                            <div className="skeleton"></div>
                            <div className="skeleton"></div>
                            <div className="skeleton"></div>
                        </div>
                    ))}
            </div>
        );
    };

    const handleRefresh = () => {
        setLoading(true); // Start skeleton loader
        setTimeout(() => {
            setLoading(false); // End skeleton loader after data "refresh"
        }, 1000); // Simulate API refresh delay (2 seconds)
    };

    const toBase64 = async (url) => {
        const response = await fetch(url);
        const blob = await response.blob();
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
        });
    };

    const paginate = (pageNumber) => setCurrentPage(pageNumber);

    const downloadInvoicePDF = async (invoice) => {
        const orderId = invoice.order_number;
        setDownloadingId(orderId);
        setDownloadedId(null);

        try {
            // Fetch user profile
            const profileRes = await fetch(`${url}/register/single/${isLoggedIn.main_id}`, {
                method: "GET",
                headers: {
                    Authorization: `Bearer ${token}`,
                }
            });
            const res = await profileRes.json();
            const profileData = res.users;

            // Convert logo to base64 (with fallback)
            let base64Logo = null;
            if (profileData?.profilePic) {
                const imageUrl = `${url}/logo/${profileData.profilePic}`;
                try {
                    base64Logo = await toBase64(imageUrl);
                } catch (error) {
                    console.warn("Logo conversion failed:", error);
                    base64Logo = "https://100billiontech.com/static/media/100BILLION_TECH_name.aad194ea6504959b3f99.png"; // optional
                }
            }
            const blob = await pdf(
                <InvoiceDocument invoice={invoice} profile={profileData} logoBase64={base64Logo} />
            ).toBlob();
            saveAs(blob, `Invoice_${invoice.invoice_number}.pdf`);

            setDownloadedId(orderId);
            setTimeout(() => setDownloadedId(null), 1200);
        } catch (err) {
            alert("Failed to download invoice");
            console.error("Download error:", err);
        } finally {
            setDownloadingId(null);
        }
    };

    // useEffect(() => {
    //     fetch("https://ecommerce.100billionoms.com/logo/1749467908154-perfumelogo.png", {
    //         mode: "cors"
    //     })
    //         .then((res) => {
    //             console.log("Fetch success:", res.status);
    //             return res.blob();
    //         })
    //         .then((blob) => console.log("Blob:", blob))
    //         .catch((err) => console.error("Fetch failed:", err));
    // }, []);
    const handleDelete = async (customerId) => {
        console.log(customerId)
        Swal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!'
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    const response = await fetch(`${url}/newinvoice/${customerId}`, {
                        method: 'DELETE',
                        headers: {
                            Authorization: `Bearer ${token}`,
                        }
                    });
                    if (!response.ok) {
                        const err = await response.json();
                        throw new Error(err?.message || 'Failed to delete');
                    }

                    Swal.fire('Deleted!', 'Invoice has been deleted.', 'success');
                    setInvoices((prev) => prev.filter((item) => item.id !== customerId));
                } catch (error) {
                    console.error('Delete error:', error.message);
                    Swal.fire('Error!', `Failed to delete invoice. ${error.message}`, 'error');
                }
            }
        });
    };

    const invoiceInSummaryDateRange = (inv) => {
        const created = invoiceActivityDate(inv);
        if (!created) return false;

        const today = new Date();
        today.setHours(0, 0, 0, 0);

        if (rangeType === "custom") {
            if (!customRange.from || !customRange.to) return true;
            const from = new Date(customRange.from);
            const to = new Date(customRange.to);
            to.setHours(23, 59, 59, 999);
            return created >= from && created <= to;
        }

        const createdDate = new Date(created);
        createdDate.setHours(0, 0, 0, 0);

        if (rangeType === "0") {
            const todayStart = new Date();
            todayStart.setHours(0, 0, 0, 0);
            const todayEnd = new Date();
            todayEnd.setHours(23, 59, 59, 999);
            return created >= todayStart && created <= todayEnd;
        }

        if (rangeType === "1") {
            const yesterday = new Date();
            yesterday.setDate(yesterday.getDate() - 1);
            yesterday.setHours(0, 0, 0, 0);
            const yesterdayEnd = new Date(yesterday);
            yesterdayEnd.setHours(23, 59, 59, 999);
            return created >= yesterday && created <= yesterdayEnd;
        }

        const diff = Math.floor(
            (today - createdDate) / (1000 * 60 * 60 * 24)
        );
        return diff <= parseInt(rangeType, 10);
    };

    useEffect(() => {
        if (rangeType !== 'custom') {
            setCustomRange({ from: '', to: '' });
        }
    }, [rangeType]);

    useEffect(() => {
        setCurrentPage(1);
    }, [rangeType, customRange.from, customRange.to]);

    const summaryInvoices = filteredInvoices.filter((inv) =>
        invoiceInSummaryDateRange(inv)
    );

    let paidAmount = 0;
    let pendingAmount = 0;
    let overdueAmount = 0;

    const todayYmd = new Date().toISOString().split("T")[0];

    summaryInvoices.forEach((inv) => {
        const outstanding = outstandingAmount(inv);
        const collected = collectedAmount(inv);
        paidAmount += collected;

        if (outstanding <= 0) return;

        const dueRaw = inv.due_date;
        let isOverdue = false;
        if (dueRaw && dueRaw !== "0000-00-00") {
            try {
                const dueYmd = new Date(dueRaw).toISOString().split("T")[0];
                isOverdue = dueYmd < todayYmd;
            } catch {
                isOverdue = false;
            }
        }

        if (isOverdue) overdueAmount += outstanding;
        else pendingAmount += outstanding;
    });

    const total = paidAmount + pendingAmount + overdueAmount;

    const paidPercent = total ? (paidAmount / total) * 100 : 0;
    const pendingPercent = total ? (pendingAmount / total) * 100 : 0;
    const overduePercent = total ? (overdueAmount / total) * 100 : 0;

    const indexOfLastCustomer = currentPage * entriesPerPage;
    const indexOfFirstCustomer = indexOfLastCustomer - entriesPerPage;
    const currentInvoices = summaryInvoices.slice(
        indexOfFirstCustomer,
        indexOfLastCustomer
    );
    const totalPages = Math.ceil(summaryInvoices.length / entriesPerPage);

    if (load) {
        return (
            <Loader />
        );
    }


    return (
        <>
            <div className="customers-header">
                <div className="customer-dropdown">
                    <p className="dashboard-title" onClick={toggleDropdown}>
                        {selectedFilter}

                        {/* All Invoices */}
                        <i className={`fa-solid fa-chevron-down ${isDropdownOpen ? 'rotate' : ''}`}></i>
                    </p>
                </div>
                <div className="custom-header-ul">
                    {isDropdownOpen && (
                        <div className="customer-dropdown-list">
                            {/* Search Bar */}
                            {/* <div className="input-container">
                                    <i className="fas fa-search"></i>
                                    <input
                                        type="search"
                                        name="search"
                                        id="search"
                                        placeholder="Search here..."
                                        value={searchQuery}
                                        onChange={handleSearch}
                                    />
                                </div> */}

                            {/* Default Filters */}
                            <div className="customer-dropdown-list-default">
                                <p className="default-filter">
                                    <i className="fa-solid fa-chevron-right"></i>
                                    Default Filters
                                </p>
                                <ul className="customer-list-ul">
                                    <li onClick={() => setSelectedFilter("All invoice")}>All invoice</li>
                                    <li onClick={() => setSelectedFilter("Active invoice")}>Active invoice</li>
                                    <li onClick={() => setSelectedFilter("Overdue invoice")}>Overdue invoice</li>
                                    <li onClick={() => setSelectedFilter("Paid invoice")}>Paid invoice</li>

                                </ul>
                            </div>

                            {/* Added by Me */}
                            {/* <div className="customer-dropdown-list-added">
                                    <p className="default-filter">
                                        <i className="fa-solid fa-chevron-right"></i>
                                        Added by Me
                                    </p>
                                    <ul className="customer-list-ul">
                                        <li onClick={() => setSelectedFilter("My invoice")}>My invoice</li>
                                        <li onClick={() => setSelectedFilter("My Active invoice")}>My Active invoice</li>
                                        <li onClick={() => setSelectedFilter("My Overdue invoice")}>My Overdue invoice</li>
                                        <li onClick={() => setSelectedFilter("My Paid invoice")}>My Paid invoice</li>
                                    </ul>
                                </div> */}
                        </div>
                    )}
                </div>

                <div className="customer-warning-new-hamburger-help">
                    <div
                        className='text-black size-8 rounded-full flex justify-center items-center bg-gray-100 hover:bg-gray-200 cursor-pointer'
                        onClick={() => setShowSearchBar(!showSearchBar)}
                    >
                        <Search size={18} />
                    </div>
                    <div className="filter-controls">
                        <button onClick={() => setShowDrawer(true)} className="more-filters-btn">
                            <i className="fa-solid fa-sliders"></i> Filters
                        </button>
                    </div>
                    <div className="import-hamburger" onClick={toggleImport}>
                        <i className="fa-solid fa-ellipsis-vertical"></i>
                    </div>
                    {isImportOpen && (
                        <div className="import-ul">
                            <ul>
                                <li onClick={() => navigate('/invoice-import')}>
                                    {/* <Link to='/invoice-import'> */}
                                    <i className="fa-solid fa-file-arrow-down"></i> Import invoice
                                    {/* </Link> */}
                                </li>
                            </ul>
                            <ul>
                                <li onClick={() => navigate('/export')}>
                                    {/* <Link to='/export'> */}
                                    <i className="fa-solid fa-file-arrow-up"></i> Export invoice
                                    {/* </Link> */}
                                </li>
                            </ul>
                            <ul>
                                <li>
                                    <a
                                        href="/bad-debt"
                                        style={{ textDecoration: "none" }}
                                    >
                                        <i className="fa-solid fa-file-arrow-down"></i> Bad Debt
                                    </a>

                                </li>
                            </ul>
                            <ul>
                                <li>
                                    <a
                                        href="/sample_invoice.xlsx"
                                        download
                                        style={{ textDecoration: "none" }}
                                    >
                                        <i className="fa-solid fa-file-arrow-down"></i> Download Sample
                                    </a>

                                </li>
                            </ul>
                            <ul>
                                <li onClick={handleRefresh}>
                                    <i className="fa-solid fa-rotate"></i> Refresh List
                                </li>
                            </ul>
                        </div>
                    )}
                </div>
            </div>

            {showSearchBar && (
                <div className="w-full my-3 p-0">
                    <div className="relative w-full">
                        <input
                            type="text"
                            placeholder="Search invoice..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            autoFocus
                            className="w-full border border-gray-300 rounded-full px-4 py-2 pr-10 focus:outline-none focus:ring-1 focus:ring-blue-500"
                        />
                        <button
                            onClick={() => {
                                setShowSearchBar(false);
                                setSearchQuery('');
                            }}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                        >
                            <X size={18} strokeWidth={2.5} />
                        </button>
                    </div>
                </div>
            )}

            <div className="bg-white rounded-lg shadow p-4 mt-4">
                {/* Header Row: Title + Filter */}
                <div className="flex flex-wrap justify-between items-center mb-3 gap-2">
                    <h2 className="text-lg font-semibold">Invoice Summary</h2>
                    <div className="flex flex-wrap gap-2 items-center">
                        <select
                            className="border rounded px-2 py-1 text-sm cursor-pointer"
                            value={rangeType}
                            onChange={e => setRangeType(e.target.value)}
                        >
                            <option value="0">Today</option>
                            <option value="1">Yesterday</option>
                            <option value="7">Last 7 Days</option>
                            <option value="30">Last 30 Days</option>
                            <option value="90">Last 90 Days</option>
                            <option value="180">Last 6 Months</option>
                            <option value="365">Last 1 Year</option>
                            <option value="custom">Custom Range</option>
                        </select>

                        {rangeType === "custom" && (
                            <>
                                <input
                                    type="date"
                                    className="border rounded px-2 py-1 text-sm"
                                    value={customRange.from}
                                    onChange={e => setCustomRange({ ...customRange, from: e.target.value })}
                                />
                                <input
                                    type="date"
                                    className="border rounded px-2 py-1 text-sm"
                                    value={customRange.to}
                                    onChange={e => setCustomRange({ ...customRange, to: e.target.value })}
                                />
                            </>
                        )}
                    </div>
                </div>

                {/* Invoice Count (same set as bar: date range + list filters/search) */}
                <p className="text-sm text-gray-500 mb-2">
                    Showing <span className="font-medium">{summaryInvoices.length}</span> invoices.
                </p>

                {/* Progress Bar */}
                {/* Animated Progress Bar */}
                <div className="w-full flex rounded overflow-hidden h-4 mb-3 bg-gray-200">
                    <motion.div
                        className="bg-green-500 h-full"
                        initial={false}
                        animate={{ width: `${paidPercent}%` }}
                        transition={{ duration: 0.6, ease: "easeInOut" }}
                        title={`Paid: ₹${paidAmount.toLocaleString()}`}
                    />
                    <motion.div
                        className="bg-yellow-400 h-full"
                        initial={false}
                        animate={{ width: `${pendingPercent}%` }}
                        transition={{ duration: 0.6, ease: "easeInOut" }}
                        title={`Pending: ₹${pendingAmount.toLocaleString()}`}
                    />
                    <motion.div
                        className="bg-red-500 h-full"
                        initial={false}
                        animate={{ width: `${overduePercent}%` }}
                        transition={{ duration: 0.6, ease: "easeInOut" }}
                        title={`Overdue: ₹${overdueAmount.toLocaleString()}`}
                    />
                </div>


                {/* Labels */}
                <div className="flex justify-between items-center text-sm text-gray-700 font-medium">
                    <span className='text-center'><span className="inline-block w-3 h-3 rounded-full bg-green-500"></span> Paid (₹{paidAmount.toLocaleString()})</span>
                    <span className='text-center'><span className="inline-block w-3 h-3 rounded-full bg-yellow-400"></span> Pending (₹{pendingAmount.toLocaleString()})</span>
                    <span className='text-center'><span className="inline-block w-3 h-3 rounded-full bg-red-500"></span> Overdue (₹{overdueAmount.toLocaleString()})</span>
                </div>
            </div>
            <div className="space-y-4 mt-5">
                {currentInvoices.map((invoice) => (
                    <div
                        key={invoice.id}
                        onClick={() => navigate(`/individual-invoice/${invoice.customer_id}`)}
                        className="
        bg-white shadow-md border border-blue-200 rounded-xl 
        p-3 mb-4 cursor-pointer transition hover:shadow-lg
        hover:border-blue-400
      "
                    >

                        {/* Top Row — View Customer + Bad Debt (Only if > 0) */}
                        <div className="flex justify-between items-center mb-1">
                            <span
                                onClick={(e) => {
                                    e.stopPropagation();
                                    navigate(`/customer-detail/${invoice.customer_id}`);
                                }}
                                className="text-sm underline text-blue-600 hover:text-blue-800 cursor-pointer"
                            >
                                View Customer
                            </span>


                            {outstandingAmount(invoice) > 0 && (
                                <span className="text-sm text-red-600 font-semibold">
                                    Bad Debt
                                </span>
                            )}
                        </div>

                        {/* Header */}
                        <div className="flex justify-between items-center mb-1 pb-1 border-b border-gray-200">
                            <p className="text-sm text-gray-600">{invoice.created_range.from} to {invoice.created_range.to}</p>
                        </div>

                        {/* Customer */}
                        <div className="text-base font-semibold text-blue-500 flex flex-col gap-0.5 tracking-wide">
                            <h2>{invoice.customer_name}</h2>
                        </div>

                        {/* Body */}
                        <div className="mt-1">
                            <p className="text-gray-700 text-sm">
                                <span className="font-semibold text-blue-500">Amount: </span>
                                Rs. {(invoice.total_grand_total).toFixed(2) || "0.00"}
                            </p>

                            {/* Show Due Amount only if pending > 0 */}
                            {invoice.total_pending > 0 && (
                                <p className="text-gray-700 mt-0.5 text-sm">
                                    <span className="font-semibold text-red-500">Due Amount: </span>
                                    Rs. {invoice.total_pending}
                                </p>
                            )}
                        </div>
                    </div>
                ))}
            </div>


            {totalPages > 1 && (

                <div className="pagination">
                    {/* First Page */}
                    <button
                        onClick={() => paginate(1)}
                        disabled={currentPage === 1}
                        className="pagination-chev-btn"
                    >
                        <ChevronsLeft size={18} />
                    </button>

                    {/* Previous Page */}
                    <button
                        onClick={() => paginate(currentPage - 1)}
                        disabled={currentPage === 1}
                        className="pagination-chev-btn"
                    >
                        <ChevronLeft size={18} />
                    </button>

                    {/* Current Page Number Only */}
                    <button className="pagination-btn active">
                        {currentPage}
                    </button>

                    {/* Next Page */}
                    <button
                        onClick={() => paginate(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className="pagination-chev-btn"
                    >
                        <ChevronRight size={18} />
                    </button>

                    {/* Last Page */}
                    <button
                        onClick={() => paginate(totalPages)}
                        disabled={currentPage === totalPages}
                        className="pagination-chev-btn"
                    >
                        <ChevronsRight size={18} />
                    </button>
                </div>
            )}
            <FilterDrawer />

            <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 z-50">
                <button
                    onClick={() => navigate("/new-invoice")}
                    className="cssbuttons-io-button">
                    <span>Create Invoice</span>
                </button>
            </div>
        </>
    );
};

export default Invoice;

export const renderNoteAsPDF = (html) => {
    const doc = parseDocument(html);
    const nodes = DomUtils.getChildren(doc);

    const renderNodes = (elements, depth = 0) => {
        return elements.map((node, idx) => {
            if (node.type === 'text') {
                return node.data;
            }

            if (node.name === 'br') {
                return <Text key={`br-${depth}-${idx}`}>{'\n'}</Text>;
            }

            if (node.name === 'p') {
                return (
                    <Text key={`p-${depth}-${idx}`} style={styles.paragraph}>
                        {renderNodes(DomUtils.getChildren(node), depth + 1)}
                    </Text>
                );
            }
            if (node.name === 'a') {
                const href = node.attribs?.href || "#";
                const linkText = DomUtils.getText(node); // 🔥 Plain text from inside <a>
                return (
                    <Link
                        key={`a-${depth}-${idx}`}
                        src={href.startsWith("http") ? href : `https://${href}`} // ensure it's clickable
                        style={{ color: 'blue', textDecoration: 'none' }} // removes underline
                    >
                        {linkText}
                    </Link>
                );
            }


            if (node.name === 'u') {
                return (
                    <Text key={`u-${depth}-${idx}`} style={{ textDecoration: 'underline' }}>
                        {renderNodes(DomUtils.getChildren(node), depth + 1)}
                    </Text>
                );
            }



            if (node.name === 'strong' || node.name === 'b') {
                return (
                    <Text key={`b-${depth}-${idx}`} style={{ fontWeight: 'bold' }}>
                        {renderNodes(DomUtils.getChildren(node), depth + 1)}
                    </Text>
                );
            }

            if (node.name === 'em' || node.name === 'i') {
                return (
                    <Text key={`i-${depth}-${idx}`} style={{ fontStyle: 'italic' }}>
                        {renderNodes(DomUtils.getChildren(node), depth + 1)}
                    </Text>
                );
            }

            if (node.name === 'ul') {
                return DomUtils.getChildren(node)
                    .filter(n => n.name === 'li')
                    .map((li, i) => (
                        <Text key={`ul-${depth}-${idx}-${i}`} style={styles.listItem}>
                            • {renderNodes(DomUtils.getChildren(li), depth + 1)}
                        </Text>
                    ));
            }

            if (node.name === 'ol') {
                return DomUtils.getChildren(node)
                    .filter(n => n.name === 'li')
                    .map((li, i) => (
                        <Text key={`ol-${depth}-${idx}-${i}`} style={styles.listItem}>
                            {`${i + 1}. ${renderNodes(DomUtils.getChildren(li), depth + 1)}`}
                        </Text>
                    ));
            }

            // fallback for unknown tags
            return renderNodes(DomUtils.getChildren(node), depth + 1);
        });
    };

    return renderNodes(nodes);
};



const styles = StyleSheet.create({
    receiptCenter: { textAlign: "center", fontSize: "18px", marginBottom: "5px", fontWeight: 600 },
    summaryRow: {
        flexDirection: "row",
        justifyContent: "space-between",
        marginTop: 4,
        fontSize: 12,
        paddingHorizontal: 10
    },
    textLogo: {
        fontSize: 20,
        fontWeight: "bold",
        color: "#003366",
        padding: 4,
        backgroundColor: "#d1dffa",
        borderRadius: 4,
        textAlign: "center"
    },
    summaryLabel: {
        fontWeight: "bold",
        fontSize: 12
    },
    summaryValue: {
        fontSize: 12
    },
    page: { padding: 20, backgroundColor: "#f9f9f9", paddingBottom: 70, },
    header: {
        flexDirection: "row", justifyContent: "space-between", alignItems: "center",
        marginBottom: 12, padding: 8, borderBottomWidth: 1, borderBottomColor: "#ddd",
        backgroundColor: "#d1dffa", gap: "15px"
    },
    headerP: { fontSize: 12, color: "#444", marginTop: "5px" },
    flex: { flex: 1 },
    headerRight: { textAlign: "right" },
    displayLogo: { display: "flex", flexDirection: "row", alignItems: "center" },
    logo1: { width: 40, marginRight: 5, objectFit: "contain" },
    logo2: { width: 100, objectFit: "contain" },
    invoiceInfo: {
        flexDirection: "row", justifyContent: "space-between",
        fontSize: 14, marginBottom: 12, marginTop: 8
    },
    invoiceFlex: { flex: 1, maxWidth: "50%" },
    invoiceFlexHeading: { fontSize: 16, fontWeight: "bold", marginBottom: 5 },
    invoiceInfoText: { marginBottom: 3, fontSize: 14 },
    row: {
        flexDirection: "row", borderBottomWidth: 1,
        paddingVertical: 4, borderBottomColor: "#ddd",
        fontSize: 14, textAlign: "left"
    },
    cell: { flex: 1, textAlign: "center", fontSize: 12 },
    cellFlex2: { flex: 2, textAlign: "center", fontSize: 12 },
    invoiceTotal: {
        textAlign: "right", fontSize: 16, fontWeight: "bold",
        marginBottom: 12, marginTop: 8, color: "#111"
    },
    invoiceNotesHeading: { fontSize: 14, marginTop: 8, marginBottom: 6, fontWeight: "600" },
    invoiceNotesLi: { fontSize: 12, color: "#555" },
    pageFooter: {
        position: 'absolute',
        bottom: 20,
        left: 0,
        right: 0,
        paddingVertical: 10,
        // borderTopWidth: 1,
        // borderTopColor: '#ccc',
        alignItems: 'center',
        justifyContent: 'center',
        // backgroundColor: '#f9f9f9',
    },
    footerPowered: {
        fontSize: 4,
        color: '#555',
        marginBottom: 2,
    },
    footerLogo: {
        width: 60,
        // height: 50,
        objectFit: 'contain'
    },
    paragraph: {
        fontSize: 12,
        marginBottom: 3,
        lineHeight: 1.1,
    },
    listItem: {
        fontSize: 12,
        marginLeft: 12,
        marginBottom: 2,
        lineHeight: 1.5,
    },


});

// const InvoiceDocument = ({ invoice, profile, logoBase64 }) => {
//     console.log(invoice);
//     if (!invoice || !profile) {
//         console.error("Invalid invoice or profile data");
//         return;
//     }
//     const url = API_BASE_URL
//     let items = [];
//     try {
//         items = Array.isArray(invoice.items)
//             ? invoice.items
//             : JSON.parse(invoice.items);
//     } catch {
//         items = [];
//     }




//     const totalAmount = invoice.grand_total;

//     return (
//         <Document>
//             <Page size="A4" style={styles.page}>
//                 <View>
//                     <Text style={styles.receiptCenter}>INVOICE</Text>
//                 </View>
//                 <View style={styles.header}>
//                     <View style={styles.flex}>
//                         <View style={styles.displayLogo}>
//                             {logoBase64 ? (
//                                 <Image src={logoBase64 || "https://100billiontech.com/static/media/100BILLION_TECH_name.aad194ea6504959b3f99.png"} style={styles.logo2} />
//                             ) : (
//                                 <Text style={styles.textLogo}>
//                                     {profile.orignal_logoname || "Your Company Name"}
//                                 </Text>
//                             )}

//                         </View>

//                         <Text style={styles.headerP}>{profile.office_address || ""}</Text>
//                     </View>
//                     <View style={[styles.flex, styles.headerRight]}>
//                         <Text style={styles.headerP}>Invoice Date: {invoice.invoice_date}</Text>
//                         <Text style={styles.headerP}>Invoice #: {invoice.invoice_number}</Text>
//                         <Text style={styles.headerP}>Order #: {invoice.order_number}</Text>
//                         <Text style={styles.headerP}>Phone No.: +91 {profile.mobile || ""}</Text>
//                         <Text style={styles.headerP}>Mail ID: {profile.email}</Text>
//                         <Text style={styles.headerP}>Tax ID: {profile.taxNo}</Text>
//                     </View>
//                 </View>

//                 <View style={styles.invoiceInfo}>
//                     <View style={styles.invoiceFlex}>
//                         <Text style={styles.invoiceFlexHeading}>Billing To:</Text>
//                         <Text style={styles.invoiceInfoText}>{invoice.customer_name}</Text>
//                     </View>
//                     {/* <View style={styles.invoiceFlex}>
//                         <Text style={styles.invoiceFlexHeading}>Due Date:</Text>
//                         <Text style={styles.invoiceInfoText}>{invoice.due_date}</Text>
//                     </View> */}
//                     {invoice.due_date && invoice.due_date !== "0000-00-00" && (
//                         <View style={styles.invoiceFlex}>
//                             <Text style={styles.invoiceFlexHeading}>Due Date:</Text>
//                             <Text style={styles.invoiceInfoText}>{invoice.due_date}</Text>
//                         </View>
//                     )}

//                 </View>

//                 <View style={[styles.row, { backgroundColor: "#d1dffa" }]}>
//                     <Text style={styles.cellFlex2}>Item</Text>
//                     <Text style={styles.cell}>Qty</Text>
//                     <Text style={styles.cell}>Rate</Text>
//                     <Text style={styles.cell}>Discount</Text>
//                     <Text style={styles.cell}>Tax</Text>
//                     <Text style={styles.cell}>Amount</Text>
//                 </View>

//                 {items.map((item, i) => (
//                     <View key={i} style={styles.row}>
//                         <Text style={styles.cellFlex2}>
//                             {item?.item?.title ? item.item.title.replace("₹", "Rs. ") : "Unnamed Item"}
//                         </Text>

//                         <Text style={styles.cell}>{item.quantity || "-"}</Text>
//                         <Text style={styles.cell}>
//                             Rs. {Number(item.rate || 0)}
//                         </Text>
//                         <Text style={styles.cell}>{item.discount || "0"}%</Text>
//                         <Text style={styles.cell}>{item.tax || "0"}%</Text>
//                         <Text style={styles.cell}>
//                             Rs. {isNaN(item.amount) ? "0.00" : Number(item.amount)}
//                         </Text>
//                     </View>
//                 ))}

//                 {/* Totals Breakdown */}
//                 <View style={{
//                     borderWidth: 1,
//                     borderColor: "#ccc",
//                     borderRadius: 6,
//                     padding: 10,
//                     marginTop: 12,
//                     backgroundColor: "#f2f6ff"
//                 }}>
//                     <Text style={{
//                         fontSize: 14,
//                         fontWeight: "bold",
//                         marginBottom: 8,
//                         textAlign: "center",
//                         color: "#003366"
//                     }}>
//                         Invoice Summary
//                     </Text>

//                     {[
//                         { label: "Subtotal", value: invoice.subtotal },
//                         { label: "Discount Amount", value: invoice.discount_amount },
//                         { label: "Advance Payment", value: invoice.advance_payment },
//                         { label: "Tax", value: invoice.tax },
//                         { label: "Shipping", value: invoice.shipping },
//                         { label: "Pending", value: invoice.pending },
//                     ].map((item, idx) => (
//                         <View
//                             key={idx}
//                             style={{
//                                 flexDirection: "row",
//                                 justifyContent: "space-between",
//                                 marginBottom: 4,
//                                 borderBottomWidth: 0.5,
//                                 borderBottomColor: "#ddd",
//                                 paddingBottom: 2
//                             }}
//                         >
//                             <Text style={{ fontSize: 12, color: "#333", fontWeight: "600" }}>{item.label}</Text>
//                             <Text style={{ fontSize: 12, color: "#222" }}>Rs. {item.value}</Text>
//                         </View>
//                     ))}
//                 </View>

//                 <View style={{
//                     marginTop: 10,
//                     padding: 10,
//                     backgroundColor: "#003366",
//                     borderRadius: 6
//                 }}>
//                     <Text style={{
//                         fontSize: 14,
//                         fontWeight: "bold",
//                         color: "white",
//                         textAlign: "right"
//                     }}>
//                         Total: Rs. {invoice.grand_total}
//                     </Text>
//                 </View>


//                 <View>
//                     <Text style={styles.invoiceNotesHeading}>Customer Note</Text>

//                     <View>
//                         {renderNoteAsPDF(invoice.customer_note || '<p>No notes</p>')}
//                     </View>



//                 </View>

//                 {/* <View style={styles.footer}>
//                     <Text style={styles.footerPowered}>Powered by</Text>
//                     <Image src={logoImgText} style={styles.footerLogo} />
//                 </View> */}

//                 <View fixed style={styles.pageFooter}>
//                     <Text style={styles.footerPowered}>Powered by</Text>
//                     <Image
//                         src={logoImgText}
//                         style={styles.footerLogo}
//                     />
//                     {/* <Text style={styles.footerText}>© {new Date().getFullYear()} Your Company Name</Text> */}
//                 </View>

//             </Page>
//         </Document>
//     );
// };


const InvoiceDocument = ({ invoice, profile, logoBase64, signUrl }) => {
    let items = [];
    try {
        items = Array.isArray(invoice.items)
            ? invoice.items
            : JSON.parse(invoice.items || "[]");
    } catch {
        items = [];
    }

    return (
        <Document>
            <Page size="A4" style={{ padding: 18, fontSize: 10 }}>

                {/* ===== TITLE ===== */}
                <View style={{ borderBottomWidth: 1, paddingBottom: 6 }}>
                    <Text style={{ fontSize: 14, fontWeight: "bold" }}>TAX INVOICE</Text>
                    <Text style={{ fontSize: 9 }}>ORIGINAL FOR RECIPIENT</Text>
                </View>

                {/* ===== HEADER BOX ===== */}
                <View style={{ flexDirection: "row", borderWidth: 1, marginTop: 6 }}>

                    {/* LEFT */}
                    <View style={{ width: "60%", padding: 8, borderRightWidth: 1 }}>
                        <View style={{ flexDirection: "row", alignItems: "center" }}>
                            {logoBase64 && (
                                <Image src={logoBase64} style={{ width: 40, marginRight: 6 }} />
                            )}
                            <Text style={{ fontSize: 11, fontWeight: "bold" }}>
                                {profile?.company_name || ""}
                            </Text>
                        </View>

                        <Text>{profile?.office_address || ""}</Text>
                        <Text>GSTIN: {profile?.taxNo || ""}</Text>
                        <Text>PAN: {profile?.pan || ""}</Text>
                        <Text>Mobile: {profile?.mobile || ""}</Text>
                    </View>

                    {/* RIGHT */}
                    <View style={{ width: "40%", padding: 8 }}>
                        <Text>Invoice No: {invoice.invoice_number || ""}</Text>
                        <Text>Invoice Date: {invoice.invoice_date || ""}</Text>
                        <Text>P.O No: {invoice.po_no || ""}</Text>
                    </View>
                </View>

                {/* ===== BILL / SHIP ===== */}
                <View style={{ flexDirection: "row", borderWidth: 1 }}>

                    <View style={{ width: "50%", padding: 8, borderRightWidth: 1 }}>
                        <Text style={{ fontWeight: "bold" }}>BILL TO</Text>
                        <Text>{invoice.customer_name || ""}</Text>
                        <Text>{invoice.billing_address || ""}</Text>
                        <Text>GSTIN: {invoice.customer_gstin || ""}</Text>
                    </View>

                    <View style={{ width: "50%", padding: 8 }}>
                        <Text style={{ fontWeight: "bold" }}>SHIP TO</Text>
                        <Text>{invoice.ship_name || ""}</Text>
                        <Text>{invoice.shipping_address || ""}</Text>
                    </View>
                </View>

                {/* ===== ITEMS TABLE ===== */}
                <View style={{ borderWidth: 1, marginTop: 6 }}>

                    {/* HEADER */}
                    <View style={{ flexDirection: "row", borderBottomWidth: 1 }}>
                        {["S.NO", "ITEMS / SERVICES", "HSN", "QTY", "RATE", "AMOUNT"].map((h, i) => (
                            <Text
                                key={i}
                                style={{
                                    flex: i === 1 ? 3 : 1,
                                    borderRightWidth: i !== 5 ? 1 : 0,
                                    padding: 4,
                                    fontWeight: "bold"
                                }}
                            >
                                {h}
                            </Text>
                        ))}
                    </View>

                    {/* ROWS */}
                    {items.map((item, i) => (
                        <View key={i} style={{ flexDirection: "row", borderBottomWidth: 1 }}>
                            <Text style={{ flex: 1, padding: 4 }}>{i + 1}</Text>
                            <Text style={{ flex: 3, padding: 4 }}>{item?.item?.title || ""}</Text>
                            <Text style={{ flex: 1, padding: 4 }}>{item.hsn || ""}</Text>
                            <Text style={{ flex: 1, padding: 4 }}>{item.quantity || ""}</Text>
                            <Text style={{ flex: 1, padding: 4 }}>{item.rate || ""}</Text>
                            <Text style={{ flex: 1, padding: 4 }}>{item.amount || ""}</Text>
                        </View>
                    ))}
                </View>

                {/* ===== TAX SUMMARY ===== */}
                <View style={{ borderWidth: 1, marginTop: 6, padding: 6 }}>
                    <Text style={{ fontWeight: "bold", marginBottom: 4 }}>Tax Summary</Text>

                    <View style={{ flexDirection: "row", borderBottomWidth: 1 }}>
                        <Text style={{ flex: 2 }}>HSN</Text>
                        <Text style={{ flex: 2 }}>Taxable</Text>
                        <Text style={{ flex: 2 }}>CGST</Text>
                        <Text style={{ flex: 2 }}>SGST</Text>
                        <Text style={{ flex: 2 }}>Total</Text>
                    </View>

                    {(invoice.tax_summary || []).map((t, i) => (
                        <View key={i} style={{ flexDirection: "row" }}>
                            <Text style={{ flex: 2 }}>{t.hsn}</Text>
                            <Text style={{ flex: 2 }}>{t.taxable}</Text>
                            <Text style={{ flex: 2 }}>{t.cgst}</Text>
                            <Text style={{ flex: 2 }}>{t.sgst}</Text>
                            <Text style={{ flex: 2 }}>{t.total}</Text>
                        </View>
                    ))}
                </View>

                {/* ===== TOTAL ===== */}
                <View style={{ borderWidth: 1, marginTop: 6, padding: 6 }}>
                    <Text style={{ fontWeight: "bold", textAlign: "right" }}>
                        TOTAL ₹ {invoice.grand_total || ""}
                    </Text>
                    <Text>Amount (in words): {invoice.amount_words || ""}</Text>
                </View>

                {/* ===== FOOTER ===== */}
                <View style={{ flexDirection: "row", borderWidth: 1, marginTop: 6 }}>

                    {/* NOTES */}
                    <View style={{ width: "40%", padding: 6, borderRightWidth: 1 }}>
                        <Text style={{ fontWeight: "bold" }}>Notes</Text>
                        {renderNoteAsPDF(invoice.customer_note || "")}
                    </View>

                    {/* BANK */}
                    <View style={{ width: "35%", padding: 6, borderRightWidth: 1 }}>
                        <Text style={{ fontWeight: "bold" }}>Bank Details</Text>
                        <Text>Name: {profile?.bank_name || ""}</Text>
                        <Text>IFSC: {profile?.ifsc || ""}</Text>
                        <Text>A/C No: {profile?.account_no || ""}</Text>
                        <Text>Bank: {profile?.bank || ""}</Text>
                    </View>

                    {/* SIGN */}
                    <View style={{ width: "25%", padding: 6, alignItems: "center" }}>
                        {signUrl && (
                            <Image src={signUrl} style={{ width: 80, height: 40 }} />
                        )}
                        <Text style={{ marginTop: 4 }}>Authorised Signatory</Text>
                    </View>
                </View>

            </Page>
        </Document>
    );
};

const InvoicePdf = () => {
    const { order_number } = useParams();
    const [invoiceData, setInvoiceData] = useState(null);
    const url = API_BASE_URL
    useEffect(() => {
        const fetchInvoice = async () => {
            try {
                const res = await fetch(`${url}/newinvoice/all`);
                const data = await res.json();
                const match = data.find(inv => String(inv.order_number) === String(order_number));
                if (match) {
                    setInvoiceData(match);
                } else {
                    alert("Invoice not found.");
                }
            } catch (error) {
                console.error("Error fetching invoice:", error);
            }
        };

        fetchInvoice();
    }, [order_number]);

    useEffect(() => {
        if (invoiceData) {
            const generate = async () => {
                const blob = await pdf(<InvoiceDocument invoice={invoiceData} />).toBlob();
                saveAs(blob, `Invoice_${invoiceData.invoice_number}.pdf`);
            };
            generate();
        }
    }, [invoiceData]);

    return null;
};
